export interface BleedBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export function displayBounds(sprite: {
  x: number;
  y: number;
  displayWidth: number;
  displayHeight: number;
  originX?: number;
  originY?: number;
}): BleedBounds {
  const originX = sprite.originX ?? 0.5;
  const originY = sprite.originY ?? 0.5;
  const left = sprite.x - sprite.displayWidth * originX;
  const top = sprite.y - sprite.displayHeight * originY;
  return { left, right: left + sprite.displayWidth, top, bottom: top + sprite.displayHeight };
}

/** The extra strip past one edge of the layout. Null when that edge has no bleed. */
export function bleedEdgeRect(
  edge: 'top' | 'bottom' | 'left' | 'right',
  stageW: number,
  stageH: number,
  bleedX: number,
  bleedY: number,
): BleedBounds | null {
  if ((edge === 'top' || edge === 'bottom') && bleedY < 1) return null;
  if ((edge === 'left' || edge === 'right') && bleedX < 1) return null;
  if (edge === 'top') return { left: -bleedX, right: stageW + bleedX, top: -bleedY, bottom: 0 };
  if (edge === 'bottom') return { left: -bleedX, right: stageW + bleedX, top: stageH, bottom: stageH + bleedY };
  if (edge === 'left') return { left: -bleedX, right: 0, top: 0, bottom: stageH };
  return { left: stageW, right: stageW + bleedX, top: 0, bottom: stageH };
}

function spanCovers(spans: Array<readonly [number, number]>, start: number, end: number): boolean {
  let cursor = start;
  for (const [from, to] of spans) {
    if (from > cursor + 1) return false;
    cursor = Math.max(cursor, to);
    if (cursor >= end - 1) return true;
  }
  return cursor >= end - 1;
}

/** True when the sprites already paint this rect, so a texture-edge strip would cover them. */
export function boundsCover(bounds: readonly BleedBounds[], rect: BleedBounds): boolean {
  const vertical = bounds
    .filter((box) => box.left <= rect.left + 1 && box.right >= rect.right - 1)
    .map((box) => [box.top, box.bottom] as const)
    .sort((a, b) => a[0] - b[0]);
  if (spanCovers(vertical, rect.top, rect.bottom)) return true;
  const horizontal = bounds
    .filter((box) => box.top <= rect.top + 1 && box.bottom >= rect.bottom - 1)
    .map((box) => [box.left, box.right] as const)
    .sort((a, b) => a[0] - b[0]);
  return spanCovers(horizontal, rect.left, rect.right);
}
